import React, { useState, useMemo } from 'react';
import {
  Plus,
  FileText,
  Clock,
  CheckCircle,
  Search,
  AlertTriangle,
  ArrowRight,
  XCircle,
  Send,
  Scale
} from 'lucide-react';
import { useUserApplication } from '../../../context/UserApplicationContext';
import { Button } from '../../../common';
import {
  isPaidApplication,
  isCompletedApplication,
  MARKETING_WIZARD_URL,
} from '../../../utils/applicationStatus';

/**
 * Application Portfolio component
 * Shows list of user's applications with ability to select or cancel.
 *
 * Quote-first funnel: new applications start on the marketing wizard
 * (markotm.com/file-trademark) — there is no self-serve "create application"
 * here any more. Continuing an application goes to the Quote & Payment section.
 */
const ApplicationPortfolio = () => {
  const {
    applications,
    loading,
    loadApplication,
    cancelApplication
  } = useUserApplication();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [applicationToCancel, setApplicationToCancel] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'quote', 'in-filing', 'completed', 'cancelled'

  const handleStartNew = () => {
    window.location.href = MARKETING_WIZARD_URL;
  };

  const confirmCancel = (app, e) => {
    e.stopPropagation(); // Prevent card click
    setApplicationToCancel(app);
    setShowCancelModal(true);
  };

  const handleCancelSubmit = async () => {
    if (!applicationToCancel) return;

    setCancelLoading(true);
    try {
      await cancelApplication(applicationToCancel.id);
      setShowCancelModal(false);
      setApplicationToCancel(null);
    } catch (error) {
      console.error('Failed to cancel application:', error);
    } finally {
      setCancelLoading(false);
    }
  };

  // Status-aware navigation: everything post-wizard lives in Quote & Payment
  const handleContinueApplication = async (application) => {
    console.log('Portfolio: Continuing application:', application.id);

    await loadApplication(application.id);
    window.location.hash = '#payments';
  };

  const calculateProgress = (app) => {
    if (app.status === 'cancelled') return 0;
    if (isCompletedApplication(app)) return 100;
    if (app.status === 'filed') return 90;
    if (app.status === 'filing_in_progress') return 80;
    if (isPaidApplication(app)) return 70;
    if (app.status === 'quote_sent') return 50;
    if (app.status === 'quote_requested') return 35;

    // Legacy applications that predate the quote flow
    let progress = 15;
    if (app.businessInfoId || app.businessName) progress += 10;
    if (app.analysisId || app.analysisCompleted) progress += 10;
    if (app.pricingId) progress += 15;
    return Math.min(progress, 100);
  };

  const getApplicationStatus = (app) => {
    if (app.status === 'cancelled')
      return { icon: XCircle, text: 'Cancelled', color: 'text-gray-400', bg: 'bg-gray-100', border: 'border-gray-200' };
    if (isCompletedApplication(app))
      return { icon: CheckCircle, text: 'Complete', color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100' };
    if (app.status === 'filed')
      return { icon: Scale, text: 'Filed', color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100' };
    if (app.status === 'filing_in_progress')
      return { icon: Scale, text: 'Filing in Progress', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' };
    if (app.status === 'pending_attorney_filing' || app.status === 'payment_completed' || (isPaidApplication(app)))
      return { icon: Scale, text: 'Pending Filing', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' };
    if (app.status === 'quote_sent')
      return { icon: Send, text: 'Quote Ready', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' };
    if (app.status === 'quote_requested')
      return { icon: Clock, text: 'Preparing Report', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' };
    if (app.status === 'payment_pending')
      return { icon: AlertTriangle, text: 'Payment Pending', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' };
    if (app.status === 'refunded')
      return { icon: XCircle, text: 'Refunded', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-100' };

    return { icon: Clock, text: 'In Progress', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' };
  };

  const getApplicationName = (app) => {
    return app.trademarkName || app.name || app.businessName || `Application ${app.id?.slice(0, 8).toUpperCase()}`;
  };

  const getTrademarkImage = (app) => {
    // Try to find an image source
    if (app.trademarkFileUrl) return app.trademarkFileUrl;
    if (app.trademarkFileData) return app.trademarkFileData;
    if (app.trademarkContent) {
      if (typeof app.trademarkContent === 'string') return app.trademarkContent;
      if (app.trademarkContent.data) return app.trademarkContent.data;
    }
    return null;
  };

  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      // Search filter
      const name = getApplicationName(app).toLowerCase();
      const matchesSearch = name.includes(searchQuery.toLowerCase());

      // Status filter
      let matchesStatus = true;
      if (statusFilter !== 'all') {
        const status = app.status || 'active';
        if (statusFilter === 'quote') {
          matchesStatus = status === 'quote_requested' || status === 'quote_sent';
        } else if (statusFilter === 'in-filing') {
          matchesStatus = isPaidApplication(app) && !isCompletedApplication(app);
        } else if (statusFilter === 'completed') {
          matchesStatus = isCompletedApplication(app);
        } else if (statusFilter === 'cancelled') {
          matchesStatus = status === 'cancelled';
        } else {
          matchesStatus = status === statusFilter;
        }
      }

      return matchesSearch && matchesStatus;
    });
  }, [applications, searchQuery, statusFilter]);

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                <div className="h-2 bg-gray-200 rounded w-full"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="relative flex-1 w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search applications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        <div className="flex gap-2 w-full sm:w-auto">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="flex-1 sm:flex-initial min-w-0 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">All Status</option>
            <option value="quote">Quote Stage</option>
            <option value="in-filing">In Filing</option>
            <option value="payment_pending">Payment Pending</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <Button
            onClick={handleStartNew}
            className="whitespace-nowrap flex items-center gap-2 bg-teal-600 hover:bg-teal-700"
          >
            <Plus size={16} />
            New Application
          </Button>
        </div>
      </div>

      {/* Applications List */}
      {filteredApplications.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
          <FileText size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications Found</h3>
          <p className="text-sm text-gray-500 mb-6">
            {searchQuery || statusFilter !== 'all'
              ? "Try adjusting your search or filters"
              : "Start with a free trademark check on our website"}
          </p>
          {(searchQuery || statusFilter !== 'all') ? (
            <Button
              variant="outline"
              onClick={() => {setSearchQuery(''); setStatusFilter('all');}}
            >
              Clear Filters
            </Button>
          ) : (
            <Button onClick={handleStartNew}>
              Start Your Free Check
            </Button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filteredApplications.map((app) => {
            const status = getApplicationStatus(app);
            const StatusIcon = status.icon;
            const appName = getApplicationName(app);
            const progress = calculateProgress(app);
            const trademarkImage = getTrademarkImage(app);
            const isCancelled = app.status === 'cancelled';

            return (
              <div
                key={app.id}
                className={`group bg-white p-4 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden transition-all duration-200 ${isCancelled ? 'opacity-60 bg-gray-50' : 'hover:shadow-md'}`}
              >
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  {/* Icon or Image */}
                  <div className={`shrink-0 ${trademarkImage ? 'h-12 w-12 rounded-lg border border-gray-100 overflow-hidden bg-gray-50' : `p-3 rounded-xl ${status.bg}`}`}>
                    {trademarkImage ? (
                      <img
                        src={trademarkImage}
                        alt={appName}
                        className={`h-full w-full object-contain ${isCancelled ? 'grayscale' : ''}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.classList.remove('h-12', 'w-12', 'border', 'border-gray-100', 'bg-gray-50', 'overflow-hidden');
                          e.target.parentElement.classList.add('p-3', 'rounded-xl', status.bg);
                          e.target.parentElement.innerHTML = '<div class="flex items-center justify-center w-full h-full text-xs text-gray-400">IMG</div>';
                        }}
                      />
                    ) : (
                      <StatusIcon className={`h-6 w-6 ${status.color}`} />
                    )}
                  </div>

                  {/* Info & Progress */}
                  <div className="flex-1 min-w-0 w-full">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <h3 className={`font-semibold truncate ${isCancelled ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{appName}</h3>
                        <span className="text-xs text-gray-400 font-mono hidden sm:inline-block">
                          • {app.id?.slice(0, 8).toUpperCase()}
                        </span>
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${status.bg} ${status.color}`}>
                        {status.text}
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ease-out ${
                            isCancelled ? 'bg-gray-300' :
                            status.text === 'Complete' ? 'bg-green-500' : 'bg-teal-500'
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-400 font-medium w-8 text-right tabular-nums">{progress}%</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 w-full sm:w-auto border-t sm:border-t-0 border-gray-100 pt-3 sm:pt-0 mt-1 sm:mt-0 justify-end sm:pl-4 sm:border-l">
                    {!isCancelled && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => confirmCancel(app, e)}
                        className="text-gray-400 hover:text-red-600 hover:bg-red-50 h-9"
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      onClick={() => handleContinueApplication(app)}
                      variant="outline"
                      className={`h-9 px-4 text-sm bg-white hover:bg-gray-50 text-gray-700 border-gray-200 shadow-sm shrink-0 ${isCancelled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={isCancelled}
                    >
                      {status.text === 'Complete' ? 'View'
                        : app.status === 'quote_sent' ? 'View Quote & Pay'
                        : 'View'}
                      <ArrowRight size={14} className="ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 text-center">
              <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Cancel Application?</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to cancel <span className="font-semibold text-gray-900">"{applicationToCancel ? getApplicationName(applicationToCancel) : 'this application'}"</span>? It will be marked as inactive.
              </p>

              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => setShowCancelModal(false)}
                  disabled={cancelLoading}
                  className="w-full"
                >
                  Go Back
                </Button>
                <Button
                  onClick={handleCancelSubmit}
                  disabled={cancelLoading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white border-transparent"
                >
                  {cancelLoading ? 'Cancelling...' : 'Cancel App'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationPortfolio;
